<template>
  <!-- ── Pill / listing mode ─────────────────────────────────── -->
  <div
    v-if="pill"
    class="wds-pill-wrap"
    :style="{ '--wds-active': activeColor }"
  >
    <span
      v-for="day in activeDays"
      :key="day.val"
      class="wds-pill"
    >
      {{ day.lbl }}
    </span>
    <span v-if="!activeDays.length" class="wds-pill-empty">—</span>
  </div>

  <!-- ── Interactive / selector mode ───────────────────────────── -->
  <div
    v-else
    class="wds-wrap"
    :class="{ 'wds-wrap--readonly': readonly, 'wds-wrap--sm': sm }"
    :style="{ '--wds-active': activeColor, '--wds-shadow': shadowColor }"
  >
    <button
      v-for="day in DAYS"
      :key="day.val"
      type="button"
      class="wds-day"
      :class="{
        'wds-day--on': isActive(day.val),
        'wds-day--blocked': isBlocked(day.val),
      }"
      :disabled="readonly || isBlocked(day.val)"
      :title="day.full"
      @click="toggle(day.val)"
    >
      {{ day.lbl }}
    </button>
  </div>
</template>

<script>
const DAYS = [
  { val: 1, lbl: 'Mon', full: 'Monday' },
  { val: 2, lbl: 'Tue', full: 'Tuesday' },
  { val: 3, lbl: 'Wed', full: 'Wednesday' },
  { val: 4, lbl: 'Thu', full: 'Thursday' },
  { val: 5, lbl: 'Fri', full: 'Friday' },
  { val: 6, lbl: 'Sat', full: 'Saturday' },
  { val: 7, lbl: 'Sun', full: 'Sunday' },
]

export default {
  name: 'WeekdaySelector',

  /** Enables v-model support */
  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    /** Array of selected day numbers (1 = Mon … 7 = Sun) */
    value: {
      type: Array,
      default: () => [],
    },
    /** Day numbers that are locked/greyed (cannot be toggled) */
    blockedDays: {
      type: Array,
      default: () => [],
    },
    /** When true, no clicks are processed (display-only mode) */
    readonly: {
      type: Boolean,
      default: false,
    },
    /**
     * Active circle fill colour.
     * Pass '#2178fb' for working-days blue or '#ff9f43' for off-days orange.
     */
    activeColor: {
      type: String,
      default: '#2178fb',
    },
    /** Box-shadow colour for the active state (default: semi-transparent blue) */
    shadowColor: {
      type: String,
      default: 'rgba(33,120,251,0.35)',
    },
    /** Compact mode — smaller circles for use inside table rows */
    sm: {
      type: Boolean,
      default: false,
    },
    /**
     * Pill mode — renders selected days as rounded-rectangle badges
     * (used in listing/table rows to show the saved configuration).
     * Only selected days are shown; unselected days are hidden.
     */
    pill: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return { DAYS }
  },

  computed: {
    /** Days that are currently selected — used in pill mode */
    activeDays() {
      return DAYS.filter(d => (this.value || []).includes(d.val))
    },
  },

  methods: {
    isActive(val) {
      return (this.value || []).includes(val)
    },
    isBlocked(val) {
      return (this.blockedDays || []).includes(val)
    },
    toggle(val) {
      if (this.readonly || this.isBlocked(val)) return
      const arr = [...(this.value || [])]
      const i = arr.indexOf(val)
      if (i === -1) arr.push(val)
      else arr.splice(i, 1)
      this.$emit('input', arr)
    },
  },
}
</script>

<style scoped>
/* ── Container ─────────────────────────────────────────────── */
.wds-wrap {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  padding: 4px 0;
}

/* ── Day button (default = unselected / white) ──────────────── */
.wds-day {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid #d8d6de;
  background: #ffffff;
  color: #6e6b7b;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.2s,
    border-color 0.2s,
    color 0.2s,
    box-shadow 0.2s,
    transform 0.12s;
  outline: none;
  -webkit-user-select: none;
  user-select: none;
  flex-shrink: 0;
  padding: 0;
  line-height: 1;
}

/* Hover on unselected day */
.wds-day:hover:not(:disabled):not(.wds-day--on):not(.wds-day--blocked) {
  border-color: var(--wds-active, #2178fb);
  color: var(--wds-active, #2178fb);
  background: rgba(33, 120, 251, 0.07);
}

/* Press animation */
.wds-day:active:not(:disabled):not(.wds-day--blocked) {
  transform: scale(0.88);
}

/* ── Selected (ON) state ────────────────────────────────────── */
.wds-day--on {
  background: var(--wds-active, #2178fb) !important;
  border-color: var(--wds-active, #2178fb) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px var(--wds-shadow, rgba(33, 120, 251, 0.35)) !important;
}

/* ── Blocked (greyed / disabled) ────────────────────────────── */
.wds-day--blocked {
  background: #f3f2f7 !important;
  border-color: #e0dce8 !important;
  color: #c4c4c4 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}

/* ── Read-only wrapper ──────────────────────────────────────── */
.wds-wrap--readonly .wds-day {
  cursor: default;
  pointer-events: none;
}

/* ── Compact / small mode (for table listings) ──────────────── */
.wds-wrap--sm {
  gap: 4px;
  flex-wrap: nowrap;
}

.wds-wrap--sm .wds-day {
  width: 28px;
  height: 28px;
  font-size: 10px;
}

/* ── Pill mode (listing / read-only display) ────────────────── */
.wds-pill-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.wds-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.04);
  color: var(--wds-active, #2178fb);
  border: 1.5px solid currentColor;
  opacity: 0.85;
  white-space: nowrap;
  line-height: 1;
}

.wds-pill-empty {
  font-size: 13px;
  color: #b9b9c3;
}
</style>
